/**
 * @file analytics.js
 * @description Utility functions for Firebase Analytics event tracking
 */

import { logEvent, setUserId, setUserProperties } from "firebase/analytics";
import { analytics } from "../firebase";

/**
 * Log an analytics event
 * @param {string} eventName - The name of the event
 * @param {object} eventParams - Optional parameters for the event
 */
export function trackEvent(eventName, eventParams = {}) {
  if (!analytics) {
    // Silently fail in environments where analytics is not available
    return;
  }

  try {
    logEvent(analytics, eventName, eventParams);
  } catch (error) {
    console.warn('Failed to log analytics event:', error);
  }
}

/**
 * Track a page view
 * @param {string} pagePath - The path of the page
 * @param {string} pageTitle - Optional title of the page
 */
export function trackPageView(pagePath, pageTitle = null) {
  if (!analytics) {
    return;
  }

  try {
    logEvent(analytics, 'page_view', {
      page_path: pagePath,
      page_title: pageTitle || pagePath,
    });
  } catch (error) {
    console.warn('Failed to log page view:', error);
  }
}

/**
 * Set the user ID for analytics
 * @param {string} userId - The user ID (typically the Firebase Auth UID)
 */
export function setAnalyticsUserId(userId) {
  if (!analytics) {
    return;
  }

  try {
    setUserId(analytics, userId);
  } catch (error) {
    console.warn('Failed to set analytics user ID:', error);
  }
}

/**
 * Clear the user ID (call on sign out)
 */
export function clearAnalyticsUserId() {
  if (!analytics) {
    return;
  }

  try {
    setUserId(analytics, null);
  } catch (error) {
    console.warn('Failed to clear analytics user ID:', error);
  }
}

/**
 * Set user properties for analytics
 * @param {object} properties - Object with property names and values
 */
export function setAnalyticsUserProperties(properties) {
  if (!analytics) {
    return;
  }

  try {
    setUserProperties(analytics, properties);
  } catch (error) {
    console.warn('Failed to set analytics user properties:', error);
  }
}

// Common event tracking functions for the app

/**
 * Track when a user signs in
 */
export function trackSignIn(method = 'google') {
  trackEvent('sign_in', { method });
}

/**
 * Track when a user signs out
 */
export function trackSignOut() {
  trackEvent('sign_out');
}

/**
 * Track when a user creates a challenge
 * @param {string} challengeId - The challenge ID
 * @param {string} challengeName - The challenge name
 */
export function trackChallengeCreated(challengeId, challengeName) {
  trackEvent('challenge_created', {
    challenge_id: challengeId,
    challenge_name: challengeName,
  });
}

/**
 * Track when a user joins a challenge
 * @param {string} challengeId - The challenge ID
 */
export function trackChallengeJoined(challengeId) {
  trackEvent('challenge_joined', {
    challenge_id: challengeId,
  });
}

/**
 * Track when a user completes a reading
 * @param {string} challengeId - The challenge ID
 * @param {string} book - The book name
 * @param {number} chapter - The chapter number
 */
export function trackReadingCompleted(challengeId, book, chapter) {
  trackEvent('reading_completed', {
    challenge_id: challengeId,
    book,
    chapter,
  });
}

/**
 * Track when a user views a challenge
 * @param {string} challengeId - The challenge ID
 */
export function trackChallengeViewed(challengeId) {
  trackEvent('challenge_viewed', {
    challenge_id: challengeId,
  });
}

/**
 * Track when a user shares a challenge
 * @param {string} challengeId - The challenge ID
 * @param {string} method - Sharing method (e.g., 'qr_code', 'link')
 */
export function trackChallengeShared(challengeId, method) {
  trackEvent('challenge_shared', {
    challenge_id: challengeId,
    method,
  });
}

